import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
      <div className="container mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="max-w-4xl mx-auto bg-card rounded-lg shadow-lg p-8 mb-8">
          <header className="mb-8 border-b pb-6">
            <h1 className="text-4xl font-bold mb-4">Cookie Policy</h1>
            <p className="text-muted-foreground">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </header>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. What Are Cookies?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help websites remember information about your visit, making your experience more personalized and the site easier to use.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Cookies can be "session cookies" (temporary, deleted when you close your browser) or "persistent cookies" (remain on your device for a set period or until you delete them).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. How We Use Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Her Season uses cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Keep you signed in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our Service</li>
                <li>Improve our Service and user experience</li>
                <li>Provide personalized content and recommendations</li>
                <li>Analyze traffic and measure marketing effectiveness</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Essential Cookies (Strictly Necessary)</h3>
              <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-2"><strong>Required:</strong> Yes | <strong>Can be disabled:</strong> No</p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-3">
                These cookies are necessary for the Service to function and cannot be disabled. They include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Authentication cookies:</strong> Keep you logged in to your account</li>
                <li><strong>Security cookies:</strong> Protect against fraudulent activity</li>
                <li><strong>Session cookies:</strong> Remember your actions during a browsing session</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Functional Cookies</h3>
              <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-2"><strong>Required:</strong> No | <strong>Can be disabled:</strong> Yes</p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-3">
                These cookies enable enhanced functionality and personalization:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Preference cookies:</strong> Remember your language, region, and display settings</li>
                <li><strong>Feature cookies:</strong> Remember which features you've used or dismissed</li>
                <li><strong>Customization cookies:</strong> Store your personalized content preferences</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.3 Analytics and Performance Cookies</h3>
              <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-2"><strong>Required:</strong> No | <strong>Can be disabled:</strong> Yes</p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-3">
                These cookies help us understand how visitors use our Service:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Usage cookies:</strong> Collect information about how you use our Service (pages visited, time spent, etc.)</li>
                <li><strong>Performance cookies:</strong> Help us identify and fix technical issues</li>
                <li><strong>Analytics cookies:</strong> Provide aggregate statistics about our Service</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                <strong>Third-party analytics:</strong> We may use services like Google Analytics to help analyze usage. These services use cookies to collect information anonymously.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.4 Advertising and Marketing Cookies</h3>
              <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-2"><strong>Required:</strong> No | <strong>Can be disabled:</strong> Yes</p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-3">
                These cookies may be used to deliver relevant advertisements:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Advertising cookies:</strong> Track your browsing to show relevant ads</li>
                <li><strong>Remarketing cookies:</strong> Show you our ads on other websites</li>
                <li><strong>Social media cookies:</strong> Allow you to share content on social platforms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Third-Party Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Some cookies on our Service are set by third-party services we use. These may include:
              </p>
              
              <div className="space-y-4 mt-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Analytics Services</h4>
                  <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1">
                    <li>Google Analytics - Usage tracking and analytics</li>
                    <li>Privacy policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Privacy Policy</a></li>
                  </ul>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Authentication Services</h4>
                  <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1">
                    <li>Supabase - User authentication and data storage</li>
                  </ul>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Payment Processors</h4>
                  <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1">
                    <li>Stripe/PayPal - Payment processing (if applicable)</li>
                  </ul>
                </div>
              </div>

              <p className="text-muted-foreground leading-relaxed mt-4">
                We do not control these third-party cookies. Please review the privacy policies of these services for more information about how they use cookies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. How to Manage Cookies</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">5.1 Browser Settings</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Most web browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Block all cookies</li>
                <li>Block third-party cookies only</li>
                <li>Delete cookies when you close your browser</li>
                <li>Make exceptions for specific websites</li>
              </ul>

              <p className="text-muted-foreground leading-relaxed mt-4 mb-3">
                To manage cookies in your browser:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and site permissions</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.2 Opt-Out Links</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                To opt out of specific tracking services:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Google Analytics:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Analytics Opt-out Browser Add-on</a></li>
                <li><strong>Advertising:</strong> <a href="http://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Your Online Choices</a> (EU)</li>
                <li><strong>Advertising:</strong> <a href="http://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Digital Advertising Alliance</a> (US)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.3 Mobile Devices</h3>
              <p className="text-muted-foreground leading-relaxed">
                On mobile devices, you can typically manage cookie preferences through your device settings or browser app settings. You may also be able to reset your advertising identifier (IDFA on iOS, AAID on Android) to limit ad tracking.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Impact of Disabling Cookies</h2>
              <div className="bg-amber-500/10 border-l-4 border-amber-500 p-4 my-4">
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Important:</strong> If you disable cookies, some features of our Service may not function properly.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Specifically, you may experience:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Difficulty staying logged in</li>
                <li>Loss of personalized settings and preferences</li>
                <li>Reduced functionality of certain features</li>
                <li>Need to re-enter information repeatedly</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Other Tracking Technologies</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                In addition to cookies, we may use other tracking technologies:
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">Web Beacons (Pixels)</h3>
              <p className="text-muted-foreground leading-relaxed">
                Small graphic images embedded in emails or web pages that allow us to track whether content has been viewed or an email has been opened.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">Local Storage</h3>
              <p className="text-muted-foreground leading-relaxed">
                HTML5 local storage allows us to store information locally on your device, similar to cookies but with greater storage capacity.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">Session Storage</h3>
              <p className="text-muted-foreground leading-relaxed">
                Temporary storage that exists only for the duration of your browsing session.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Updates to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our practices. We will notify you of significant changes by posting the updated policy on our Service and updating the "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. More Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                For more information about how we handle your personal data, please review our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="mt-4 p-4 bg-secondary/30 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Her Season</strong><br />
                  Email: privacy@auralife.com<br />
                  Address: 123 Wellness Street, Bloom City, BC 12345
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default CookiePolicy;
